import { classes } from './array';
import { i18nInstance } from '../i18n';
import { language } from '@tencent/wphk-i18n';
import { get } from '../utils/basic';
import { isFunction } from './is';

export const bigCamelize = (s: string): string => camelize(s).replace(s.charAt(0), s.charAt(0).toUpperCase());

export const camelize = (s: string): string => s.replace(/-(\w)/g, (_: any, p: string) => p.toUpperCase());

export const kebabCase = (s: string): string => {
  const ret = s.replace(/([A-Z])/g, ' $1').trim();
  return ret.split(' ').join('-')
    .toLowerCase();
};

export const slash = (path: string) => {
  const isExtendedLengthPath = path.startsWith('\\\\?\\');

  if (isExtendedLengthPath) {
    return path;
  }

  return path.replace(/\\/g, '/');
};

export type BEM<S extends string | undefined, N extends string, NC extends string> = S extends undefined
  ? NC
  : S extends `$--${infer CM}`
  ? `${N}--${CM}`
  : S extends `--${infer M}`
  ? `${NC}--${M}`
  : `${NC}__${S}`

export function createNamespaceFn<N extends string>(namespace: N) {
  return <C extends string>(name: C) => {
    const componentName = `${namespace}-${name}` as const;

    const createBEM = <S extends string | undefined = undefined>(
      suffix?: S,
    ): BEM<S, typeof namespace, typeof componentName> => {
      if (!suffix) {
        return componentName as any;
      }

      if (suffix[0] === '$') {
        return suffix.replace('$', namespace) as any;
      }

      return (suffix.startsWith('--') ? `${componentName}${suffix}` : `${componentName}__${suffix}`) as any;
    };

    const cssVars = (suffix: string) => `--${namespace}-${name}-${suffix}`;

    const createTransalte = (name: string) => {
      const prefix = `${camelize(name)}.`;

      return (path: string, ...args: unknown[]) => {
        const lang = language.getLanguage();
        const messages = i18nInstance.getConfig()[lang] || {};
        const message = get(messages, prefix + path) || get(messages, path);

        return isFunction(message) ? message(...args) : message;
      };
    };

    return {
      name: bigCamelize(componentName),
      n: createBEM,
      t: createTransalte(name),
      classes,
      cssVars,
    };
  };
}
